(function () {
  const selectors = {
    header: '[data-site-header]',
    mobileToggle: '[data-mobile-toggle]',
    mobileDrawer: '[data-mobile-drawer]',
    searchToggle: '[data-search-toggle]',
    searchPanel: '[data-search-panel]',
    cartToggle: '[data-open-cart]',
    cartDrawer: '[data-cart-drawer]',
    cartScrim: '[data-cart-scrim]',
    cartClose: '[data-close-cart]',
    cartCount: '[data-cart-count]',
    localeDetails: '[data-locale-details]',
    scrollUi: '[data-scroll-ui]',
    scrollProgress: '[data-scroll-progress]',
    backToTop: '[data-back-to-top]',
    ajaxCartForm: 'form[data-ajax-cart]',
    productForm: '[data-product-form]',
    productGallery: '[data-product-gallery]',
    productRecommendations: '[data-product-recommendations]',
    productFloatingBar: '[data-product-floating-bar]'
  };

  const routes = window.ApexFoxTheme?.routes || {};
  let lastFocusedElement = null;
  let revealObserver = null;

  function qs(selector, scope = document) {
    return scope.querySelector(selector);
  }

  function qsa(selector, scope = document) {
    return Array.from(scope.querySelectorAll(selector));
  }

  function setHidden(element, hidden) {
    if (!element) return;
    if (hidden) {
      element.setAttribute('hidden', '');
    } else {
      element.removeAttribute('hidden');
    }
  }

  function updateHeaderState() {
    const header = qs(selectors.header);
    if (!header) return;
    header.classList.toggle('is-scrolled', window.scrollY > 8);
  }

  function updateScrollUi() {
    const scrollTop = Math.max(window.scrollY || document.documentElement.scrollTop || 0, 0);
    const scrollMax = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
    const progress = Math.min(scrollTop / scrollMax, 1);
    const isVisible = scrollTop > 420;

    document.documentElement.style.setProperty('--scroll-progress', progress.toFixed(4));
    qs(selectors.scrollUi)?.classList.toggle('is-visible', isVisible);
    qsa(selectors.backToTop).forEach((button) => button.classList.toggle('is-visible', isVisible));
    qsa(selectors.scrollProgress).forEach((node) => {
      node.style.transform = `scaleX(${progress})`;
    });
  }

  function openCart() {
    const drawer = qs(selectors.cartDrawer);
    const scrim = qs(selectors.cartScrim);
    if (!drawer || !scrim) return;
    lastFocusedElement = document.activeElement;
    setHidden(scrim, false);
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-open');
    qs(selectors.cartClose, drawer)?.focus();
  }

  function closeCart() {
    const drawer = qs(selectors.cartDrawer);
    const scrim = qs(selectors.cartScrim);
    if (!drawer || !scrim) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    setHidden(scrim, true);
    document.documentElement.classList.remove('cart-drawer-open');
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  function showToast(message, href) {
    let toast = qs('[data-theme-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'theme-toast';
      toast.setAttribute('data-theme-toast', '');
      document.body.appendChild(toast);
    }

    toast.innerHTML = '';
    const span = document.createElement('span');
    span.textContent = message;
    toast.appendChild(span);

    if (href) {
      const link = document.createElement('a');
      link.href = href;
      link.textContent = 'View cart';
      toast.appendChild(link);
    }

    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timeout);
    showToast.timeout = window.setTimeout(() => toast.classList.remove('is-visible'), 3600);
  }

  async function refreshCartDrawer() {
    if (!routes.cartUrl) return null;
    const response = await fetch(`${routes.cartUrl}?sections=cart-drawer`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return null;
    const sections = await response.json();
    const html = sections['cart-drawer'];
    const current = document.getElementById('shopify-section-cart-drawer');
    if (html && current) {
      current.innerHTML = html;
    }
    return fetch(`${routes.cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((cartResponse) => cartResponse.json());
  }

  function updateCartCount(count) {
    qsa(selectors.cartCount).forEach((node) => {
      node.textContent = count;
      node.toggleAttribute('hidden', count < 1);
    });
  }

  async function handleAjaxCartSubmit(event) {
    const form = event.currentTarget;
    if (!window.fetch || !routes.cartAdd) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const originalText = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding...';
    }

    try {
      const response = await fetch(routes.cartAdd, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Unable to add item.');
      }

      const cart = await refreshCartDrawer();
      if (cart) updateCartCount(cart.item_count || 0);
      showToast('Added to cart', routes.cartUrl);
      openCart();
    } catch (error) {
      showToast(error.message || 'Unable to add item.', routes.cartUrl);
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  }

  function formatMoney(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    return (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    });
  }

  function initProductForm(form) {
    const dataNode = qs('[data-variants-json]', form);
    if (!dataNode) return;

    let variants = [];
    try {
      variants = JSON.parse(dataNode.textContent);
    } catch (error) {
      return;
    }

    const idInput = qs('[name="id"]', form);
    const addButton = qs('[type="submit"]', form);
    const priceNode = qs('[data-product-price]', form.closest('[data-product-shell]') || form);
    const compareNode = qs('[data-product-compare]', form.closest('[data-product-shell]') || form);
    const moneyFormat = form.getAttribute('data-money-format') || '${{amount}}';

    function currentOptions() {
      return qsa('[data-option-position]', form).map((group) => {
        const checked = qs('input:checked', group);
        const select = qs('select', group);
        return checked ? checked.value : select ? select.value : '';
      });
    }

    function setVariant() {
      const selected = currentOptions();
      const variant = selected.length
        ? variants.find((item) => item.options.every((value, index) => value === selected[index]))
        : variants.find((item) => String(item.id) === String(idInput?.value)) || variants[0];
      if (!variant) {
        if (idInput) idInput.value = '';
        if (addButton) {
          addButton.disabled = true;
          addButton.textContent = window.ApexFoxTheme?.strings?.unavailable || 'Unavailable';
        }
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (priceNode) priceNode.textContent = formatMoney(variant.price, moneyFormat);
      if (compareNode) {
        compareNode.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price, moneyFormat) : '';
        compareNode.toggleAttribute('hidden', !(variant.compare_at_price > variant.price));
      }
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available
          ? window.ApexFoxTheme?.strings?.addToCart || 'Add to cart'
          : window.ApexFoxTheme?.strings?.soldOut || 'Sold out';
      }
    }

    qsa('input, select', form).forEach((input) => input.addEventListener('change', setVariant));
    setVariant();
  }

  function initProductGalleries(scope = document) {
    qsa(selectors.productGallery, scope).forEach((gallery) => {
      if (gallery.dataset.productGalleryReady === 'true') return;

      const items = qsa('[data-product-gallery-item]', gallery);
      const mediaStage = qs('.product-gallery', gallery);
      const previousButton = qs('[data-product-gallery-previous]', gallery);
      const nextButton = qs('[data-product-gallery-next]', gallery);
      const status = qs('[data-product-gallery-status]', gallery);
      if (!items.length) return;

      gallery.dataset.productGalleryReady = 'true';
      let activeIndex = Math.max(items.findIndex((item) => !item.hidden), 0);
      let touchStartX = null;

      function showItem(index) {
        activeIndex = (index + items.length) % items.length;
        items.forEach((item, itemIndex) => {
          const isActive = itemIndex === activeIndex;
          item.hidden = !isActive;
          item.classList.toggle('is-active', isActive);
        });

        if (status) status.textContent = `${activeIndex + 1} / ${items.length}`;
      }

      previousButton?.addEventListener('click', () => showItem(activeIndex - 1));
      nextButton?.addEventListener('click', () => showItem(activeIndex + 1));

      mediaStage?.addEventListener('touchstart', (event) => {
        if (event.touches.length === 1) touchStartX = event.touches[0].clientX;
      }, { passive: true });

      mediaStage?.addEventListener('touchend', (event) => {
        if (touchStartX === null || !event.changedTouches.length) return;
        const swipeDistance = event.changedTouches[0].clientX - touchStartX;
        touchStartX = null;
        if (Math.abs(swipeDistance) < 45) return;
        showItem(swipeDistance < 0 ? activeIndex + 1 : activeIndex - 1);
      }, { passive: true });

      mediaStage?.addEventListener('touchcancel', () => {
        touchStartX = null;
      }, { passive: true });

      showItem(activeIndex);
    });
  }

  function initProductRecommendations(scope = document) {
    qsa(selectors.productRecommendations, scope).forEach((host) => {
      if (host.dataset.productRecommendationsReady === 'true' || !host.dataset.url) return;
      host.dataset.productRecommendationsReady = 'true';

      async function loadRecommendations() {
        try {
          const response = await fetch(host.dataset.url, { headers: { Accept: 'text/html' } });
          if (!response.ok) return;
          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
          const refreshedHost = qs(selectors.productRecommendations, documentFragment);
          if (!refreshedHost || !refreshedHost.innerHTML.trim()) {
            host.remove();
            return;
          }
          host.innerHTML = refreshedHost.innerHTML;
          qsa(selectors.ajaxCartForm, host).forEach((form) => form.addEventListener('submit', handleAjaxCartSubmit));
          initScrollReveal(host);
        } catch (error) {
          host.remove();
        }
      }

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          loadRecommendations();
        }, { rootMargin: '240px 0px' });
        observer.observe(host);
      } else {
        loadRecommendations();
      }
    });
  }

  function updateFloatingBarOffset() {
    const bars = qsa(selectors.productFloatingBar);
    const height = bars.reduce((max, bar) => Math.max(max, bar.offsetHeight || 0), 0);
    document.body.classList.toggle('has-floating-product-bar', height > 0);
    document.documentElement.style.setProperty('--product-floating-bar-offset', height ? `${height + 8}px` : '0px');
  }

  function initProductFloatingBars(scope = document) {
    const bars = qsa(selectors.productFloatingBar, scope);
    if (!bars.length) {
      updateFloatingBarOffset();
      return;
    }

    bars.forEach((bar) => {
      if (bar.dataset.floatingProductReady === 'true') return;
      bar.dataset.floatingProductReady = 'true';

      const form = qs('[data-floating-product-form]', bar);
      const variantSelect = qs('[data-floating-variant-select]', bar);
      const priceNode = qs('[data-floating-product-price]', bar);
      const compareNode = qs('[data-floating-product-compare]', bar);
      const submitButton = qs('[data-floating-submit]', bar);
      const moneyFormat = form?.getAttribute('data-money-format') || '${{amount}}';
      const triggerElement = qs('[data-product-shell]');

      function showAfterScroll() {
        const triggerTop = triggerElement
          ? triggerElement.getBoundingClientRect().top + window.scrollY + Math.min(triggerElement.offsetHeight * 0.5, 720)
          : 520;
        bar.classList.toggle('is-visible', window.scrollY > triggerTop);
      }

      function updateSelectedVariant() {
        if (!variantSelect) return;
        const option = variantSelect.options[variantSelect.selectedIndex];
        if (!option) return;

        const price = Number(option.dataset.price || 0);
        const comparePrice = Number(option.dataset.comparePrice || 0);
        const available = option.dataset.available === 'true';

        if (priceNode) priceNode.textContent = formatMoney(price, moneyFormat);
        if (compareNode) {
          compareNode.textContent = comparePrice > price ? formatMoney(comparePrice, moneyFormat) : '';
          compareNode.toggleAttribute('hidden', !(comparePrice > price));
        }
        if (submitButton) {
          submitButton.disabled = !available;
          submitButton.textContent = available
            ? submitButton.dataset.availableLabel || window.ApexFoxTheme?.strings?.addToCart || 'Add to cart'
            : submitButton.dataset.soldOutLabel || window.ApexFoxTheme?.strings?.soldOut || 'Sold out';
        }
      }

      variantSelect?.addEventListener('change', updateSelectedVariant);
      window.addEventListener('scroll', showAfterScroll, { passive: true });
      window.addEventListener('resize', () => {
        updateFloatingBarOffset();
        showAfterScroll();
      });

      updateSelectedVariant();
      updateFloatingBarOffset();
      showAfterScroll();
    });
  }

  function getRevealObserver() {
    if (revealObserver) return revealObserver;

    revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        entry.target.classList.add('is-visible');
        entry.target.addEventListener('transitionend', () => {
          entry.target.classList.add('is-revealed');
        }, { once: true });
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.12
    });

    return revealObserver;
  }

  function initScrollReveal(scope = document) {
    const root = scope === document ? qs('#MainContent') : scope;
    if (!root) return;

    const revealSelector = [
      '.section-heading',
      '.media-text__copy',
      '.media-text__media',
      '.feature-list li',
      '.product-card',
      '.category-tile',
      '.trust-item',
      '.video-showcase__copy',
      '.video-showcase__media',
      '.spec-cell',
      '.faq-item',
      '.newsletter-band__inner',
      '.article-card',
      '.article-main .rte',
      '.product-gift',
      '.product-service',
      '.product-accordion details',
      '.product-highlights',
      '.product-highlight',
      '.collection-toolbar',
      '.empty-state',
      '.search-form',
      '.cart-line',
      '.cart-summary',
      '.template-content__stat',
      '.template-content-card',
      '.ot16-overview__copy',
      '.ot16-overview__media',
      '.ot16-metric',
      '.ot16-promise',
      '.ot16-split__copy',
      '.ot16-split__media',
      '.ot16-app__copy',
      '.ot16-app__media',
      '.ot16-app-card',
      '.ot16-compare-table tr',
      '.ot16-spec-table tr',
      '.ot16-final-cta__inner',
      '.product-performance-metric',
      '.product-performance__media',
      '.product-specs-card',
      '.product-accessory-card'
    ].join(',');

    const explicitCandidates = qsa(revealSelector, root);
    const sectionCandidates = qsa([
      '.shopify-section > section > .container > *',
      '.shopify-section > section > .container--wide > *',
      '.shopify-section > .section > .container > *',
      '.shopify-section > .section > .container--wide > *',
      '.site-main > .section > .container > *',
      '.site-main > section > .container > *'
    ].join(','), root);
    const candidates = Array.from(new Set([...explicitCandidates, ...sectionCandidates])).filter((element) => {
      if (element.classList.contains('scroll-reveal')) return false;
      if (element.closest('.hero-mix, .hero-sport, .product-modal, .product-popup, .cart-drawer')) return false;
      if (element.closest('.product-main--commerce') && !element.matches('.product-highlights, .product-highlight, .product-gift, .product-service, .product-accordion details')) return false;
      if (element.matches('script, style, link, template, [hidden]')) return false;
      return true;
    });
    if (!candidates.length) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canObserve = 'IntersectionObserver' in window;
    const candidateSet = new Set(candidates);
    const observer = !reducedMotion && canObserve ? getRevealObserver() : null;

    candidates.forEach((element) => {
      const siblings = Array.from(element.parentElement?.children || []).filter((child) => candidateSet.has(child));
      const siblingIndex = Math.max(0, siblings.indexOf(element));
      const delay = Math.min(siblingIndex, 5) * 55;

      element.style.setProperty('--reveal-delay', `${delay}ms`);
      element.classList.add('scroll-reveal');

      const rect = element.getBoundingClientRect();
      const startsNearViewport = rect.top < window.innerHeight * 0.72 && rect.bottom > 0;
      if (startsNearViewport) {
        element.classList.add('is-visible', 'is-revealed');
        return;
      }

      if (!observer) {
        element.classList.add('is-visible', 'is-revealed');
        return;
      }

      observer.observe(element);
    });

    window.setTimeout(() => {
      candidates.forEach((element) => {
        if (element.classList.contains('is-visible')) return;
        const rect = element.getBoundingClientRect();
        const isNearViewport = rect.top < window.innerHeight * 1.08 && rect.bottom > 0;
        if (isNearViewport) element.classList.add('is-visible', 'is-revealed');
      });
    }, 420);
  }

  function initHeroMix() {
    qsa('[data-hero-mix]').forEach((hero) => {
      const slides = qsa('[data-hero-slide]', hero);
      if (!slides.length) return;

      let current = slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (current < 0) current = 0;
      hydrateHeroSlide(slides[current]);
      if (slides.length < 2) return;

      window.setInterval(() => {
        slides[current].classList.remove('is-active');
        current = (current + 1) % slides.length;
        hydrateHeroSlide(slides[current]);
        slides[current].classList.add('is-active');
      }, 5200);
    });
  }

  function hydrateHeroSlide(slide) {
    if (!slide || slide.dataset.videoHydrated === 'true') return;

    const template = qs('[data-hero-video-template]', slide);
    if (!template) return;

    slide.dataset.videoHydrated = 'true';
    const fragment = template.content.cloneNode(true);
    const media = fragment.firstElementChild;
    if (media) {
      media.setAttribute('aria-hidden', 'true');
      slide.appendChild(media);
    }
  }

  function scheduleNonCritical(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 1800 });
      return;
    }

    window.setTimeout(callback, 600);
  }

  function scheduleHeroMedia(callback) {
    const run = () => window.setTimeout(callback, 3600);

    if (document.readyState === 'complete') {
      run();
      return;
    }

    window.addEventListener('load', run, { once: true });
  }

  function openOverlay(element) {
    if (!element) return;
    lastFocusedElement = document.activeElement;
    setHidden(element, false);
    element.classList.add('is-open');
    document.documentElement.classList.add('theme-modal-open');
    qs('[data-product-modal-close], [data-product-popup-close]', element)?.focus();
  }

  function closeOverlay(element) {
    if (!element) return;
    element.classList.remove('is-open');
    setHidden(element, true);
    document.documentElement.classList.remove('theme-modal-open');
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  function closeAllProductOverlays() {
    qsa('[data-product-modal].is-open, [data-product-popup-modal].is-open').forEach(closeOverlay);
  }

  function initProductModals(scope = document) {
    qsa('[data-product-modal-open]', scope).forEach((button) => {
      if (button.dataset.productModalReady === 'true') return;
      button.dataset.productModalReady = 'true';
      button.addEventListener('click', () => {
        const modalId = button.getAttribute('data-product-modal-open');
        openOverlay(document.getElementById(modalId));
      });
    });

    qsa('[data-product-modal-close]', scope).forEach((button) => {
      if (button.dataset.productModalCloseReady === 'true') return;
      button.dataset.productModalCloseReady = 'true';
      button.addEventListener('click', () => closeOverlay(button.closest('[data-product-modal]')));
    });
  }

  function initProductOfferPopups(scope = document) {
    qsa('[data-product-popup]', scope).forEach((host) => {
      if (host.dataset.productPopupReady === 'true') return;
      host.dataset.productPopupReady = 'true';

      const modal = qs('[data-product-popup-modal]', host);
      const storageKey = `apexProductPopup:${host.dataset.popupId || 'default'}`;
      const isEditor = Boolean(window.Shopify?.designMode);

      function openPopup() {
        openOverlay(modal);
      }

      function closePopup() {
        closeOverlay(modal);
        if (!isEditor) {
          try {
            window.sessionStorage.setItem(storageKey, 'closed');
          } catch (error) {
            // Storage can be unavailable in some privacy modes.
          }
        }
      }

      qs('[data-product-popup-open]', host)?.addEventListener('click', openPopup);
      qsa('[data-product-popup-close]', host).forEach((button) => button.addEventListener('click', closePopup));

      if (host.dataset.popupAuto === 'true') {
        let wasClosed = false;
        try {
          wasClosed = window.sessionStorage.getItem(storageKey) === 'closed';
        } catch (error) {
          wasClosed = false;
        }
        if (!wasClosed || isEditor) {
          const delay = Number(host.dataset.popupDelay || 3000);
          window.setTimeout(openPopup, Math.max(800, delay));
        }
      }
    });
  }

  function init() {
    updateHeaderState();
    updateScrollUi();
    window.addEventListener('scroll', () => {
      updateHeaderState();
      updateScrollUi();
    }, { passive: true });
    window.addEventListener('resize', updateScrollUi);

    qsa(selectors.mobileToggle).forEach((button) => {
      button.addEventListener('click', () => {
        const drawer = qs(selectors.mobileDrawer);
        const isHidden = drawer?.hasAttribute('hidden');
        setHidden(drawer, !isHidden);
        button.setAttribute('aria-expanded', String(Boolean(isHidden)));
      });
    });

    qsa(selectors.searchToggle).forEach((button) => {
      button.addEventListener('click', () => {
        const panel = qs(selectors.searchPanel);
        const isHidden = panel?.hasAttribute('hidden');
        setHidden(panel, !isHidden);
        button.setAttribute('aria-expanded', String(Boolean(isHidden)));
        if (isHidden) qs('input[type="search"]', panel)?.focus();
      });
    });

    document.addEventListener('click', (event) => {
      if (event.target.closest(selectors.cartToggle)) {
        event.preventDefault();
        openCart();
      }
      if (event.target.closest(selectors.cartClose) || event.target.matches(selectors.cartScrim)) {
        closeCart();
      }
      qsa(selectors.localeDetails).forEach((details) => {
        if (!details.contains(event.target)) details.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeCart();
        closeAllProductOverlays();
        setHidden(qs(selectors.searchPanel), true);
        qsa(selectors.localeDetails).forEach((details) => details.removeAttribute('open'));
      }
    });

    qsa(selectors.ajaxCartForm).forEach((form) => form.addEventListener('submit', handleAjaxCartSubmit));
    qsa(selectors.productForm).forEach(initProductForm);
    initProductGalleries();
    initProductRecommendations();
    initProductFloatingBars();
    initProductModals();
    initProductOfferPopups();
    initScrollReveal();
    window.setTimeout(initScrollReveal, 260);
    scheduleHeroMedia(initHeroMix);

    qsa(selectors.backToTop).forEach((button) => button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }));

    document.addEventListener('shopify:section:load', (event) => {
      initScrollReveal(event.target);
      qsa(selectors.ajaxCartForm, event.target).forEach((form) => form.addEventListener('submit', handleAjaxCartSubmit));
      qsa(selectors.productForm, event.target).forEach(initProductForm);
      initProductGalleries(event.target);
      initProductRecommendations(event.target);
      initProductFloatingBars(event.target);
      initProductModals(event.target);
      initProductOfferPopups(event.target);
    });

    document.addEventListener('shopify:section:select', (event) => {
      initScrollReveal(event.target);
    });

    window.addEventListener('pageshow', () => {
      initScrollReveal();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
